// src/pages/admin/purchase-order/AllPurchaseOrders.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import AuthService from '../../../services/authService'
import { usePermissions } from '../../../hooks/usePermissions'
import CreatePurchaseOrderModal from '../../../components/modals/CreatePurchaseOrderModal'
import ViewPurchaseOrderModal from '../../../components/modals/ViewPurchaseOrderModal'
import DeletePurchaseOrderModal from '../../../components/modals/DeletePurchaseOrderModal'
import InlineLoader from '../../../components/ui/InlineLoader'
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2,
  AlertCircle,
  ShoppingCart,
  RefreshCw,
  Download,
  Calendar,
  Building,
  User,
  Package,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  X
} from 'lucide-react'
import PurchaseOrderService from '../../../services/purchaseOrderService'
import { PurchaseOrderUtils } from '../../../utils/purchaseOrderUtils'

// Enhanced Toast Component
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-in slide-in-from-right-full duration-300 ${
    type === 'success' 
      ? 'bg-green-500 text-white' 
      : type === 'error'
      ? 'bg-red-500 text-white'
      : type === 'warning'
      ? 'bg-yellow-500 text-white'
      : 'bg-blue-500 text-white'
  }`}>
    {type === 'success' ? <CheckCircle className="h-5 w-5" /> : 
     type === 'error' ? <XCircle className="h-5 w-5" /> : 
     <AlertCircle className="h-5 w-5" />}
    <span className="font-medium">{message || 'Action completed'}</span>
    <button onClick={onClose} className="ml-2 hover:bg-black hover:bg-opacity-20 rounded p-1">
      <X className="h-4 w-4" />
    </button>
  </div>
)

// Enhanced Stats Card Component
// eslint-disable-next-line no-unused-vars
const StatsCard = ({ icon: Icon, title, value, color = "orange" }) => (
  <Card className="hover:shadow-md transition-shadow duration-200">
    <CardContent className="p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
)

const AllPurchaseOrders = () => {
  // User and permissions
  const currentUser = AuthService.getCurrentUser()
  // eslint-disable-next-line no-unused-vars
  const permissions = usePermissions(currentUser)
  
  // Debug: Log the full user object to understand its structure
  console.log('🔍 Debug - Full current user object:', currentUser)
  console.log('🔍 Debug - User properties:', Object.keys(currentUser || {}))
  
  // Use the correct property structure based on working components
  const userBranchId = currentUser?.branchId
  const isManager = currentUser?.role?.toLowerCase() === 'manager'
  const isWarehouse = currentUser?.role?.toLowerCase() === 'warehouse'
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin'
  const isSuperAdmin = currentUser?.role?.toLowerCase() === 'superadmin' || currentUser?.role?.toLowerCase() === 'super admin'
  const canApprove = isAdmin || isSuperAdmin
  
  console.log('🔐 AllPurchaseOrders - User Access:', {
    user: currentUser?.name || currentUser?.username,
    role: currentUser?.role,
    branchId: userBranchId,
    isManager: isManager,
    fullUserObject: currentUser
  })

  // State management
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(true)
  // eslint-disable-next-line no-unused-vars
  const [actionLoading, setActionLoading] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [supplierTypeFilter, setSupplierTypeFilter] = useState('')
  const [branchFilter, setBranchFilter] = useState(
    (isManager || isWarehouse) ? (userBranchId || '') : ''
  )
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [toast, setToast] = useState(null)
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    totalItems: 0
  })

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Dropdown data
  const [branches, setBranches] = useState([])
  // eslint-disable-next-line no-unused-vars
  const [suppliers, setSuppliers] = useState([])
  // eslint-disable-next-line no-unused-vars
  const [warehouses, setWarehouses] = useState([])

  // Filter options
  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'deleted', label: 'Deleted' },
  ]

  const supplierTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'supplier', label: 'Supplier' },
    { value: 'warehouse', label: 'Warehouse' }
  ]

  // Enhanced status configuration to ensure no question marks
  const getCleanStatusConfig = (status) => {
    // Normalize status to lowercase and trim whitespace
    const normalizedStatus = status?.toString().toLowerCase().trim()
    
    const statusMap = {
      'pending': {
        label: 'Pending',
        icon: '⏰',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      },
      'approved': {
        label: 'Approved',
        icon: '✅',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      },
      'completed': {
        label: 'Completed',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'complete': {
        label: 'Completed',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      },
      'cancelled': {
        label: 'Cancelled',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      'rejected': {
        label: 'Rejected',
        icon: '❌',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      },
      'deleted': {
        label: 'DELETED',
        icon: '🗑️',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300'
      }
    }

    // Return the matching status or default with checkmark for any completed-like status
    const matchedStatus = statusMap[normalizedStatus]
    if (matchedStatus) {
      return matchedStatus
    }

    // If status contains "complete" anywhere, use the completed style with checkmark
    if (normalizedStatus?.includes('complete')) {
      return {
        label: 'Completed',
        icon: '✅',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      }
    }

    // Default fallback
    return {
      label: status || 'Unknown',
      icon: '❓',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    }
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Calculate stats - also handle completed status variations
  const calculateStats = (orders) => {
    const stats = {
      total: orders.length,
      pending: orders.filter(order => order.status?.toLowerCase() === 'pending').length,
      completed: orders.filter(order => {
        const status = order.status?.toLowerCase()
        return status === 'completed' || status === 'complete' || status?.includes('complete')
      }).length,
      totalItems: orders.reduce((sum, order) => sum + (order.orderItems?.length || 0), 0)
    }
    return stats
  }

  // Enhanced refresh function
  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered')
    setLoading(true)
    
    try {
      const response = await PurchaseOrderService.getAllPurchaseOrders()
      
      if (response && response.success) {
        console.log('✅ Purchase orders refreshed successfully:', response.data)
        const ordersData = response.data.purchaseOrders || response.data || []
        setPurchaseOrders(ordersData)
        setFilteredOrders(ordersData)
        setStats(calculateStats(ordersData))
      }
      
      showToast('Data refreshed successfully', 'success')
    } catch (error) {
      console.error('❌ Error during refresh:', error)
      showToast('Error refreshing data', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch purchase orders from API
  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true)
      console.log('🛒 Fetching purchase orders...')
      
      // For managers and warehouse users, add branch filter to API call
      let apiParams = {}
      
      if ((isManager || isWarehouse) && userBranchId) {
        apiParams.branchId = userBranchId
        console.log(`🏢 ${isWarehouse ? 'Warehouse' : 'Manager'} branch filter applied:`, {
          branchId: userBranchId,
          branchName: 'Branch filtering enabled'
        })
      } else if (isManager || isWarehouse) {
        console.warn(`⚠️ ${isWarehouse ? 'Warehouse' : 'Manager'} user but no branch ID found:`, {
          userBranchId,
          currentUser
        })
      }
      
      const response = await PurchaseOrderService.getAllPurchaseOrders(apiParams)
      
      if (response && response.success) {
        console.log('✅ Purchase orders fetched successfully:', response.data)
        const ordersData = response.data.purchaseOrders || response.data || []
        
        // Additional frontend filtering for managers and warehouse users as security layer
        let filteredData = ordersData
        if ((isManager || isWarehouse) && userBranchId) {
          filteredData = ordersData.filter(order => {
            const orderBranchId = order.requestingBranch?._id || order.requestingBranch
            return orderBranchId === userBranchId
          })
          console.log(`🔒 ${isWarehouse ? 'Warehouse' : 'Manager'} frontend filter applied:`, {
            totalOrders: ordersData.length,
            filteredOrders: filteredData.length,
            userBranchId: userBranchId,
            filteringBy: userBranchId
          })
        }
        
        setPurchaseOrders(filteredData)
        setFilteredOrders(filteredData)
        setStats(calculateStats(filteredData))
      } else {
        console.error('❌ Failed to fetch purchase orders:', response)
        showToast('Failed to fetch purchase orders', 'error')
      }
    } catch (error) {
      console.error('❌ Error fetching purchase orders:', error)
      showToast('Error fetching purchase orders', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      const [branchesResponse, suppliersResponse] = await Promise.all([
        PurchaseOrderService.getAllBranches(),
        PurchaseOrderService.getSuppliersAndWarehouses()
      ])
      
      if (branchesResponse?.success) {
        setBranches(branchesResponse.data?.branches || [])
      }
      
      if (suppliersResponse?.success) {
        setSuppliers(suppliersResponse.data?.suppliers || [])
        setWarehouses(suppliersResponse.data?.warehouses || [])
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error)
    }
  }

  // Filter and search logic
  const clearFilters = () => {
    console.log('🧹 Clearing all filters')
    setSearchTerm('')
    setStatusFilter('')
    setSupplierTypeFilter('')
    // Don't reset branch filter for managers and warehouse users
    if (!isManager && !isWarehouse) {
      setBranchFilter('')
    }
  }

  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      await fetchPurchaseOrders()
      await fetchDropdownData()
    }
    initializeData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apply filters when dependencies change
  useEffect(() => {
    const filterData = () => {
      let filtered = [...purchaseOrders]
      
      console.log('🔍 Applying filters to purchase orders:', {
        totalOrders: purchaseOrders.length,
        searchTerm,
        statusFilter,
        supplierTypeFilter,
        branchFilter,
        isManager,
        userBranchId: userBranchId
      })

      if (searchTerm) {
        filtered = PurchaseOrderUtils.filterItems(filtered, searchTerm, [
          'poNumber', 
          'supplier.name', 
          'warehouse.name', 
          'requestingBranch.name',
          'createdBy.username'
        ])
        console.log(`📝 Search filter applied: ${filtered.length} orders match "${searchTerm}"`)
      }

      if (statusFilter) {
        filtered = filtered.filter(order => order.status === statusFilter)
        console.log(`📊 Status filter applied: ${filtered.length} orders with status "${statusFilter}"`)
      }

      if (supplierTypeFilter) {
        filtered = filtered.filter(order => order.supplierType === supplierTypeFilter)
        console.log(`🏭 Supplier type filter applied: ${filtered.length} orders of type "${supplierTypeFilter}"`)
      }

      if (branchFilter) {
        filtered = filtered.filter(order => {
          const orderBranchId = order.requestingBranch?._id || order.requestingBranch
          return orderBranchId === branchFilter
        })
        console.log(`🏢 Branch filter applied: ${filtered.length} orders for branch "${branchFilter}"`)
      }

      console.log(`✅ Final filtered results: ${filtered.length} purchase orders`)
      setFilteredOrders(filtered)
    }
    
    filterData()
  }, [purchaseOrders, searchTerm, statusFilter, supplierTypeFilter, branchFilter, isManager, userBranchId])

  // Handle view order
  const handleViewOrder = (order, event) => {
    if (event) event.stopPropagation()
    setSelectedOrder(order)
    setShowViewModal(true)
  }

  // Handle edit order
  const handleEditOrder = (order, event) => {
    if (event) event.stopPropagation()
    setSelectedOrder(order)
    setShowEditModal(true)
  }

  // Handle delete order
  const handleDeleteOrder = (order, event) => {
    if (event) event.stopPropagation()
    setSelectedOrder(order)
    setShowDeleteModal(true)
  }

  // Handle approve order
  const handleApproveOrder = async (order, event) => {
    if (event) event.stopPropagation()
    
    try {
      setActionLoading(prev => ({ ...prev, [order._id]: true }))
      
      const response = await PurchaseOrderService.updatePurchaseOrder(
        order._id,
        { status: 'approved' }
      )
      
      if (response && response.success) {
        showToast('Purchase order approved successfully!', 'success')
        fetchPurchaseOrders()
      } else {
        showToast('Failed to approve purchase order', 'error')
      }
    } catch (error) {
      console.error('Error approving purchase order:', error)
      showToast('Error approving purchase order', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [order._id]: false }))
    }
  }

  // Handle reject order
  const handleRejectOrder = async (order, event) => {
    if (event) event.stopPropagation()
    
    try {
      setActionLoading(prev => ({ ...prev, [order._id]: true }))
      
      const response = await PurchaseOrderService.updatePurchaseOrder(
        order._id,
        { status: 'rejected' }
      )
      
      if (response && response.success) {
        showToast('Purchase order rejected successfully!', 'success')
        fetchPurchaseOrders()
      } else {
        showToast('Failed to reject purchase order', 'error')
      }
    } catch (error) {
      console.error('Error rejecting purchase order:', error)
      showToast('Error rejecting purchase order', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [order._id]: false }))
    }
  }

  // Handle order created/updated
  // eslint-disable-next-line no-unused-vars
  const handleOrderCreated = (order) => {
    showToast(`Purchase order ${selectedOrder ? 'updated' : 'created'} successfully!`)
    fetchPurchaseOrders()
  }

  // Handle order deleted
  // eslint-disable-next-line no-unused-vars
  const handleOrderDeleted = (orderId) => {
    showToast('Purchase order deleted successfully!')
    fetchPurchaseOrders()
  }

  // Export to CSV
  const handleExport = () => {
    const exportData = filteredOrders.map(order => ({
      'PO Number': order.poNumber || 'N/A',
      'Supplier/Warehouse': order.supplier?.name || order.warehouse?.name || 'N/A',
      'Type': order.supplierType,
      'Branch': order.requestingBranch?.name || 'N/A',
      'Order Date': PurchaseOrderUtils.formatDate(order.orderDate),
      'Expected Delivery': PurchaseOrderUtils.formatDate(order.expectedDeliveryDate),
      'Status': order.status,
      'Total Items': order.orderItems?.length || 0,
      'Created By': order.createdBy?.username || 'N/A',
      'Created Date': PurchaseOrderUtils.formatDate(order.createdAt, 'time')
    }))

    const columns = [
      { key: 'PO Number', header: 'PO Number' },
      { key: 'Supplier/Warehouse', header: 'Supplier/Warehouse' },
      { key: 'Type', header: 'Type' },
      { key: 'Branch', header: 'Branch' },
      { key: 'Order Date', header: 'Order Date' },
      { key: 'Expected Delivery', header: 'Expected Delivery' },
      { key: 'Status', header: 'Status' },
      { key: 'Total Items', header: 'Total Items' },
      { key: 'Created By', header: 'Created By' },
      { key: 'Created Date', header: 'Created Date' }
    ]

    PurchaseOrderUtils.exportToCSV(exportData, 'purchase-orders', columns)
    showToast('Purchase orders exported successfully!')
  }

  if (loading) {
    return (
      <InlineLoader text="Loading purchase orders..." />
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Enhanced Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Purchase Orders</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center space-x-2 border-gray-300 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={handleExport}
            className="flex items-center space-x-2"
            disabled={filteredOrders.length === 0}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={ShoppingCart}
          title="Total Orders"
          value={stats.total.toLocaleString()}
          color="blue"
        />
        <StatsCard
          icon={Clock}
          title="Pending Orders"
          value={stats.pending.toLocaleString()}
          color="yellow"
        />
        <StatsCard
          icon={CheckCircle}
          title="Completed Orders"
          value={stats.completed.toLocaleString()}
          color="green"
        />
        <StatsCard
          icon={Package}
          title="Total Items"
          value={stats.totalItems.toLocaleString()}
          color="purple"
        />
      </div>

      {/* Enhanced Filters Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Orders</h2>
            </div>
            {(searchTerm || statusFilter || supplierTypeFilter || branchFilter) && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search PO or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Supplier Type Filter */}
            <div className="relative">
              <select
                value={supplierTypeFilter}
                onChange={(e) => setSupplierTypeFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                {supplierTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Branch Filter */}
            <div className="relative">
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                disabled={isManager || isWarehouse}
                className={`w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white ${
                  (isManager || isWarehouse) ? 'cursor-not-allowed opacity-75 bg-gray-50' : ''
                }`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.75rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="">All Branches</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              {(isManager || isWarehouse) && (
                <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                  <span className="text-xs text-gray-500 bg-white px-1">
                    Your Branch Only
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
            Showing <span className="font-medium text-gray-900">{filteredOrders.length}</span> of <span className="font-medium text-gray-900">{purchaseOrders.length}</span> orders
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Purchase Orders Table */}
      <Card className="shadow-sm">
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center space-x-2">
            <ShoppingCart className="h-5 w-5 text-orange-600" />
            <span>Orders Directory</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredOrders.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <table className="w-full min-w-full bg-white">
                  {/* Table Header */}
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PO Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Supplier/Warehouse
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  {/* Table Body */}
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order) => {
                      const statusConfig = getCleanStatusConfig(order.status)
                      
                      return (
                        <tr 
                          key={order._id} 
                          className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                          onClick={() => handleViewOrder(order)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <ShoppingCart className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 font-mono">{order.poNumber || 'N/A'}</p>
                                <p className="text-sm text-gray-500">ID: {order._id.slice(-8).toUpperCase()}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900">
                                  {order.supplier?.name || order.warehouse?.name || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500 capitalize">
                                  {order.supplierType}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-gray-900">
                              {order.requestingBranch?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {PurchaseOrderUtils.formatDate(order.orderDate)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                              {/* <span className="mr-1">{statusConfig.icon}</span> */}
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Package className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900 font-medium">
                                {order.orderItems?.length || 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-900">
                                {order.createdBy?.username || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-left justify-left space-x-1">
                              <button
                                onClick={(e) => handleViewOrder(order, e)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              
                              {order.status === 'pending' && canApprove && (
                                <>
                                  <button
                                    onClick={(e) => handleApproveOrder(order, e)}
                                    disabled={actionLoading[order._id]}
                                    className="text-gray-400 hover:text-green-600 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Approve Order"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => handleRejectOrder(order, e)}
                                    disabled={actionLoading[order._id]}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Reject Order"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              
                              {order.status === 'pending' && (
                                <button
                                  onClick={(e) => handleEditOrder(order, e)}
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                  title="Edit Order"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              
                              <button
                                onClick={(e) => handleDeleteOrder(order, e)}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete Order"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredOrders.map((order) => {
                  const statusConfig = getCleanStatusConfig(order.status)
                  
                  return (
                    <Card key={order._id} className="shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Order Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <ShoppingCart className="h-4 w-4 text-orange-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 font-mono">{order.poNumber || 'N/A'}</p>
                                <p className="text-sm text-gray-500">ID: {order._id.slice(-8)}</p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                              {statusConfig.label}
                            </span>
                          </div>

                          {/* Order Details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500">Supplier/Warehouse</p>
                              <p className="text-gray-900 font-medium">
                                {order.supplier?.name || order.warehouse?.name || 'N/A'}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Branch</p>
                              <p className="text-gray-900">{order.requestingBranch?.name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Order Date</p>
                              <p className="text-gray-900">{PurchaseOrderUtils.formatDate(order.orderDate)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Items</p>
                              <p className="text-gray-900 font-medium">{order.orderItems?.length || 0}</p>
                            </div>
                          </div>

                          {/* Created By */}
                          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <div className="flex items-center space-x-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Created by {order.createdBy?.username || 'N/A'}
                              </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => handleViewOrder(order, e)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              
                              {order.status === 'pending' && canApprove && (
                                <>
                                  <button
                                    onClick={(e) => handleApproveOrder(order, e)}
                                    disabled={actionLoading[order._id]}
                                    className="text-gray-400 hover:text-green-600 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Approve Order"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => handleRejectOrder(order, e)}
                                    disabled={actionLoading[order._id]}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Reject Order"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                              
                              {order.status === 'pending' && (
                                <button
                                  onClick={(e) => handleEditOrder(order, e)}
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded transition-colors"
                                  title="Edit Order"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              
                              <button
                                onClick={(e) => handleDeleteOrder(order, e)}
                                className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                title="Delete Order"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 px-6">
              <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {purchaseOrders.length === 0 ? 'No purchase orders found' : 'No orders match your filters'}
              </h3>
              <p className="text-gray-600 mb-4">
                {purchaseOrders.length === 0 
                  ? 'Create your first purchase order to get started.' 
                  : 'Try adjusting your search criteria.'}
              </p>
              {purchaseOrders.length === 0 && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Purchase Order
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <CreatePurchaseOrderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onOrderCreated={handleOrderCreated}
        mode="create"
      />

      <CreatePurchaseOrderModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedOrder(null)
        }}
        onOrderCreated={handleOrderCreated}
        editOrder={selectedOrder}
        mode="edit"
      />

      <ViewPurchaseOrderModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false)
          setSelectedOrder(null)
        }}
        purchaseOrder={selectedOrder}
      />

      <DeletePurchaseOrderModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedOrder(null)
        }}
        purchaseOrder={selectedOrder}
        onOrderDeleted={handleOrderDeleted}
      />
    </div>
  )
}

export default AllPurchaseOrders